/* Le regole della pagina del singolo dispaccio (/article/[id]/[slug],
   components/article/DispaccioView). Nate col modello «Notizia», che
   quella pagina ha sostituito il 15/10/2026.

   Il riferimento è il pacchetto uploads/Consegna_Notizia_OI.zip
   (Notizia_proposta_OI.html più le istruzioni). Le misure del
   riferimento sono in /tmp/pwmeas/notizia/rif.js.

   Modulo senza direttiva: lo leggono la pagina (Server Component) e
   DispaccioView (client). Una costante esportata da un file "use client" e
   letta dal server non è il suo valore, vedi LEAD_DISPACCI nei
   learnings. */

import type { NewsItem, Priority, TemaRedazione, TipoDerivato } from "@/lib/types";

/* ───────────────────────────────────────────────────────────────
   L'immagine d'apertura
   ─────────────────────────────────────────────────────────────── */

/** Che cosa sta nel riquadro d'apertura. `composizione` è la tavola
 *  grafica OI del riferimento (components/article/CompostaOI.tsx), `foto`
 *  è l'immagine del dispaccio. */
export type ImmagineNotizia =
  | { tipo: "composizione" }
  | { tipo: "foto"; src: string };

/** Quale immagine apre la notizia.
 *
 *  La foto del dispaccio ha la precedenza: `image_url` a monte è
 *  valorizzato su tutti i dispacci non d'archivio. La composizione OI
 *  resta come ripiego per chi la foto non ce l'ha, cioè in pratica tutto
 *  l'archivio storico (tipo Archivio, mai un `image_url`), e per una
 *  stringa vuota o di soli spazi. Fino al 06/10/2026 apriva sempre la
 *  composizione, come chiedeva il pacchetto («usare temporaneamente la
 *  composizione OI del prototipo»).
 */
export function immagineNotizia(item: Pick<NewsItem, "image">): ImmagineNotizia {
  const src = item.image?.trim();
  return src ? { tipo: "foto", src } : { tipo: "composizione" };
}

/* ───────────────────────────────────────────────────────────────
   Gli approfondimenti
   ─────────────────────────────────────────────────────────────── */

/** Una scheda di «Per approfondire». Tutti campi del dispaccio vero: il
 *  tipo resta il suo (niente notizie promosse a report), la foto è la sua
 *  `image_url` o nessuna. */
export type Approfondimento = {
  id: string;
  href: string;
  titolo: string;
  sommario: string;
  tipo: Priority;
  territorio: string | null;
  foto: string | null;
  /** Il tipo derivato dalla cronologia, accanto al tipo di monte;
   *  `null` se il conteggio non è arrivato (e allora non si stampa). */
  tipoDerivato: TipoDerivato | null;
  /** La rubrica di monte, spesso vuota: le schede `n-corr` della notizia
   *  aperta (modello del 19/10/2026) ne prendono il puntino del tema. */
  categoria: string;
  /** «5 ottobre», nel fuso di Roma. */
  data: DataNotizia;
};

/** Una voce dei rulli in fondo alla notizia aperta (`.n-grande` di
 *  uploads/notizia-aperta.html): tutti campi del dispaccio vero. */
export type VoceRullo = {
  id: string;
  href: string;
  titolo: string;
  sommario: string;
  tipo: Priority;
  categoria: string;
  territorio: string | null;
  foto: string | null;
  data: DataNotizia;
};

/** Le tre liste grezze che arrivano da monte (`rulliPer` in
 *  lib/notizia-dati.ts), più lunghe di quanto serve: la scelta delle voci
 *  da mostrare la fa `componiRulli`, dopo aver tolto quelle già in pagina. */
export type RulliGrezzi = {
  /** Stesso Paese e stesso tema (di redazione se c'è, altrimenti la
   *  rubrica); vuota se manca il Paese o tutti e due i temi. */
  tema: VoceRullo[];
  /** Il tema di redazione da cui viene `tema`; `null` se `tema` viene
   *  dalla rubrica (o è vuota). */
  temaRedazione: TemaRedazione | null;
  /** Stesso Paese, qualunque rubrica. */
  paese: VoceRullo[];
  /** Gli ultimi segnali deboli, da tutto il rullo. */
  segnali: VoceRullo[];
  /** Quanti segnali deboli ci sono a monte (`meta.pagination.total`). */
  segnaliTotale: number | null;
};

/** Quello che la fascia rende. */
export type RulliNotizia = {
  /** Le due schede grandi: stesso Paese e stessa rubrica, oppure solo
   *  stesso Paese se la rubrica a monte è vuota (`tema` dice quale). */
  grandi: VoceRullo[];
  tema: boolean;
  /** Il tema di redazione delle schede grandi, quando sono quelle. */
  temaRedazione: TemaRedazione | null;
  altre: VoceRullo[];
  segnali: VoceRullo[];
  segnaliTotale: number | null;
};

/** Le voci dei rulli senza doppioni e senza quelle già in pagina
 *  (`escludi`: il dispaccio stesso e «Per approfondire»). Le schede
 *  grandi preferiscono chi ha la foto, nell'ordine di uscita; le liste
 *  sotto sono titoli e la foto non conta. */
export function componiRulli(r: RulliGrezzi, escludi: Iterable<string>): RulliNotizia {
  const visti = new Set(escludi);
  const prendi = (voci: VoceRullo[], quante: number, conFoto: boolean): VoceRullo[] => {
    const libere = voci.filter((v) => !visti.has(v.id));
    const ordinate = conFoto ? [...libere.filter((v) => v.foto), ...libere.filter((v) => !v.foto)] : libere;
    const scelte = ordinate.slice(0, quante);
    scelte.forEach((v) => visti.add(v.id));
    return scelte;
  };
  const tema = r.tema.length > 0;
  const grandi = prendi(tema ? r.tema : r.paese, 2, true);
  const altre = prendi(r.paese, 3, false);
  const segnali = prendi(r.segnali, 3, false);
  const conTema = tema && grandi.length > 0;
  return {
    grandi,
    tema: conTema,
    temaRedazione: conTema ? r.temaRedazione : null,
    altre,
    segnali,
    segnaliTotale: r.segnaliTotale,
  };
}

/** Una scheda di «Altri casi aperti» (`.nc-caso3` del disegno chiuso):
 *  un altro filone ancora in corso, presentato dal suo dispaccio più
 *  recente, che è anche l'ultima tappa. Tutti dati del dispaccio vero. */
export type AltroCaso = {
  id: string;
  href: string;
  /** Il nome del filone (`occhiello`); senza, il titolo del dispaccio. */
  nome: string;
  /** Il titolo dell'ultima tappa quando `nome` è il filone, altrimenti
   *  il sommario: la riga sotto il nome dice sempre una cosa nuova. */
  testo: string;
  tipoDerivato: "caso" | "report";
  tappe: number;
  paese: string | null;
  categoria: string;
  foto: string | null;
  /** «9 ottobre», nel fuso di Roma: la data dell'ultima tappa. */
  ultima: DataNotizia;
};

/** Le date della testata, già scritte dal server nel fuso di Roma: una
 *  data formattata nel browser darebbe un'altra stringa (fuso e motore
 *  diversi) e un errore d'idratazione. */
export type DataNotizia = { iso: string; testo: string };
